<template>
  <div class="office-list-page">
    <section class="offices">
      <div class="container bg-white">
        <OfficeSearchBar
          ref="searchbar"
          @updated="handleSearchUpdated"
          @showMobileMap="handleShowMobileMap"
          @askOffer="handleOffer"
        />
        <OfficeMap
          ref="map"
          :offices="offices"
          @updated="handleMapUpdated"
          @askOffer="handleOffer"
        />
        <OfficeList
          ref="list"
          :offices="offices"
          @highlight="handleHighlight"
          @askOffer="handleOffer"
        />
      </div>
    </section>
    <OfferModal ref="offerModal" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Loader } from "@googlemaps/js-api-loader";
import OfficeSearchBar from "@/components/offices/OfficeSearchBar.vue";
import OfficeMap from "@/components/offices/OfficeMap.vue";
import OfficeList from "@/components/offices/OfficeList.vue";
import OfferModal from "@/components/offices/OfferModal.vue";

let updateTimeout;

const districtMappings = {
  I: { lat: 47.497921, lng: 19.032891 },
  II: { lat: 47.523054, lng: 19.016723 },
  III: { lat: 47.555269, lng: 19.044959 },
  IV: { lat: 47.572289, lng: 19.094312 },
  V: { lat: 47.502144, lng: 19.050573 },
  VI: { lat: 47.511024, lng: 19.066743 },
  VII: { lat: 47.502412, lng: 19.073977 },
  VIII: { lat: 47.491816, lng: 19.088366 },
  IX: { lat: 47.470848, lng: 19.090852 },
  X: { lat: 47.483541, lng: 19.156649 },
  XI: { lat: 47.473439, lng: 19.033659 },
  XII: { lat: 47.501439, lng: 18.989334 },
  XIII: { lat: 47.53803, lng: 19.069134 },
  XIV: { lat: 47.524083, lng: 19.117955 },
  XV: { lat: 47.558725, lng: 19.119472 },
  XVI: { lat: 47.521099, lng: 19.199549 },
  XVII: { lat: 47.480328, lng: 19.267601 },
  XVIII: { lat: 47.433289, lng: 19.206443 },
  XIX: { lat: 47.449154, lng: 19.142582 },
  XX: { lat: 47.433232, lng: 19.127016 },
  XXI: { lat: 47.416001, lng: 19.07936 },
  XXII: { lat: 47.410692, lng: 19.013688 },
  XXIII: { lat: 47.390097, lng: 19.125348 }
};

export default {
  name: "Offices",
  components: { OfficeSearchBar, OfficeMap, OfficeList, OfferModal },
  data() {
    return {
      filters: {},
      sort: null,
      google: null
      // selectedOffice: null,
    };
  },
  computed: {
    ...mapGetters({ offices: "offices/officeList" })
  },
  created() {
    document.querySelector("body").classList.add("disable-scroll");
    const loader = new Loader({
      apiKey: process.env.VUE_APP_MAPS_API_KEY,
      version: "weekly",
      libraries: ["places"]
    });

    loader.load().then(googleInstance => {
      this.google = googleInstance;
      this.$refs.map.initGoogle(googleInstance);
      this.$refs.searchbar.initGoogle(googleInstance);
    });
  },
  beforeRouteLeave(to, from, next) {
    document.querySelector("body").classList.remove("disable-scroll");
    next();
  },
  beforeRouteEnter(to, from, next) {
    document.querySelector("body").classList.add("disable-scroll");
    next();
  },
  activated() {
    this.$root.$emit("refreshHeader");
  },
  methods: {
    handleMapUpdated(lat, long, zoom, dist) {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        const distanceInKm = Math.ceil(
          (38000 / Math.pow(2, zoom - 3)) * Math.cos((lat * Math.PI) / 180)
        );
        // const distanceInKm = Math.round(distanceInMeters / 1000);
        const query = {
          filters: {
            lat: { $eq: lat },
            long: { $eq: long },
            dist: { $eq: dist }
          }
        };

        if (this.filters.offieRatingFrom) {
          query.filters.offieRating = { $gte: this.filters.offieRatingFrom };
        }

        if (this.filters.offieRatingTo) {
          if (query.filters.offieRating) {
            query.filters.offieRating.$lte = this.filters.offieRatingTo;
          } else {
            query.filters.offieRating = { $lte: this.filters.offieRatingTo };
          }
        }

        if (this.filters.priceFrom) {
          query.filters.price = { $gte: this.filters.priceFrom };
        }

        if (this.filters.priceTo) {
          if (query.filters.price) {
            query.filters.price.$lte = this.filters.priceTo;
          } else {
            query.filters.price = { $lte: this.filters.priceTo };
          }
        }

        if (this.filters.district) {
          query.filters.district = { $eq: this.filters.district };
        }

        if (this.filters.undergroundGarage) {
          query.filters.undergroundGarage = {
            $eq: this.filters.undergroundGarage
          };
        }

        if (this.filters.class) {
          query.filters.class = { $eq: this.filters.class };
        }

        if (this.sort) {
          query.sort = this.sort;
        }

        this.$store.dispatch("offices/fetchOffices", query).then(() => {
          this.$refs.list.scrollToTop();
        });
      }, 500);
    },
    handleSearchUpdated(filters, sort) {
      this.filters = filters;
      this.sort = sort;

      if (this.filters.place) {
        this.$refs.map.setBounds(this.filters.place);
      }

      if (this.filters.maxDistance) {
        this.$refs.map.setZoomFromDistance(this.filters.maxDistance);
      }

      if (this.filters.district?.length && !this.filters.place) {
        if (this.filters.district?.length == 1) {
          const coordinates = districtMappings[this.filters.district[0]];
          this.$refs.map.setCenter(
            new this.google.maps.LatLng(coordinates.lat, coordinates.lng)
          );
          this.$refs.map.setZoom(14);
        } else {
          var bounds = new this.google.maps.LatLngBounds();
          this.filters.district.forEach(d => {
            const coordinates = districtMappings[d];
            bounds.extend({ lat: coordinates.lat, lng: coordinates.lng });
          });
          this.$refs.map.fitBounds(bounds);
        }
      }
    },
    handleHighlight(office) {
      this.$refs.map.highligh(office);
    },
    handleOffer(office) {
      this.$refs.offerModal.open();
    },
    handleShowMobileMap(showMap) {
      this.$refs.map.showOnMobile(showMap);
      this.$refs.list.showOnMobile(showMap);
    }
  }
};
</script>
<style lang="scss">
.offices {
  .container {
    @media (max-width: $breakpoint2) {
      display: block;
      top: 0;
      height: 100%;
      margin: 0;
      padding: 0;
    }

    @media (max-width: 1200px) {
      margin: 0;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "search search"
        "map list";
    }

    height: calc(100vh);

    @media (min-width: $breakpoint) {
      height: calc(100vh - 66px);
      top: 66px;
    }

    position: fixed;
    box-sizing: border-box;
    // height: calc(100vh - 66px);
    width: 100vw;
    left: 0;

    max-width: initial;
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr 1fr 550px;
    grid-template-areas:
      "search search search"
      "map map list";
    gap: 20px;
    padding: 20px;
  }
}
</style>
